import {ComponentType, FC, ReactNode, useMemo} from 'react';

import {createView, view, type View} from '@sail/ui';

import {
  Conversation,
  Observability,
} from '@stripe-internal/conversations-client';
import {InlineDataComponentRegistry} from '@stripe-internal/inline-data';

import {AppCloseHandler, AppConfig, AppProfile} from '../core/types';
import {AppLiveChatIframe} from '../core/components/AppLiveChatIframe';
import {AssistantContainer} from '../core/components/AssistantContainer';
import {AssistantScreen} from '../core/components/AssistantScreen';
import {DefaultErrorBoundary} from '../core/components/ErrorBoundary';
import {ParentMessageBus} from '../core/lib/parentMessageBus/ParentMessageBus';
import {StripeAssistantDisclaimer} from './components/Footer/StripeAssistantDisclaimer';
import {StripeAssistantHeader} from './components/Header/StripeAssistantHeader';
import {StripeAssistantProviders} from './providers/StripeAssistantProviders';
import {useVisibility} from '../core/hooks/appHooks';

type Props = View.IntrinsicElement<
  'div',
  {
    config: AppConfig;
    profile: AppProfile;
    withLiveChat?: boolean;
    clientFetch?: typeof fetch;
    parentMessageBus?: ParentMessageBus;
    header?: ReactNode | ComponentType;
    footer?: ReactNode | ComponentType;
    log?: ReactNode | ComponentType<{conversation: Conversation}>;
    input?: ReactNode | ComponentType<{conversation: Conversation}>;
    inlineDataComponents?: InlineDataComponentRegistry;
    errorBoundary?: ComponentType<{children: ReactNode}> | null;
    onClose?: AppCloseHandler;
    observability?: Observability;
  }
>;

function AssistantScreenWhenOpen({
  log,
  input,
}: {
  log?: ReactNode | ComponentType<{conversation: Conversation}>;
  input?: ReactNode | ComponentType<{conversation: Conversation}>;
}) {
  const {visibility} = useVisibility();

  if (visibility === 'closed') {
    return null;
  }

  return <AssistantScreen log={log} input={input} />;
}

export const StripeAssistant: FC<View.ViewProps<Props>> = createView<Props>(
  ({
    config,
    profile,
    withLiveChat = false,
    clientFetch,
    parentMessageBus,
    header = StripeAssistantHeader,
    footer = StripeAssistantDisclaimer,
    log,
    input,
    inlineDataComponents,
    errorBoundary,
    onClose,
    observability,
    ...props
  }) => {
    const defaultErrorBoundary = useMemo(() => {
      return ({children}: {children: ReactNode}) => (
        <DefaultErrorBoundary config={config}>{children}</DefaultErrorBoundary>
      );
    }, [config]);

    // not using ?? as we want to allow for overriding with null
    const errorBoundaryOrDefault =
      errorBoundary === undefined ? defaultErrorBoundary : errorBoundary;

    return (
      <view.div
        data-testid="stripe-assistant-root"
        css={{height: 'fill', overflow: 'hidden'}}
        {...props}
      >
        <StripeAssistantProviders
          config={config}
          profile={profile}
          clientFetch={clientFetch}
          parentMessageBus={parentMessageBus}
          inlineDataComponents={inlineDataComponents}
          onClose={onClose}
          observability={observability}
        >
          <AssistantContainer
            header={header}
            footer={footer}
            errorBoundary={errorBoundaryOrDefault}
          >
            <AssistantScreenWhenOpen log={log} input={input} />
            {withLiveChat ? <AppLiveChatIframe /> : null}
          </AssistantContainer>
        </StripeAssistantProviders>
      </view.div>
    );
  },
);
